import JSCookie from 'js-cookie';
import {
    useCallback,
    useContext,
    useMemo,
    useState,
    createContext,
    ReactNode,
    useEffect,
} from 'react';
import { useSearchParams } from 'wouter';

type SiteContextType = {
    isKiosk: boolean | null;
    setIsKiosk: ((val: boolean | null) => void) | null;
};

export const SiteContext = createContext<SiteContextType>({
    isKiosk: null,
    setIsKiosk: null,
});

export const useSiteContext = (): SiteContextType => useContext(SiteContext);

export const useSite = () => {
    const { isKiosk = null, setIsKiosk = null } = useSiteContext() || {};
    return { isKiosk, setIsKiosk };
};

interface SiteProviderProps {
    children: ReactNode;
    isKiosk?: boolean | null;
}

export const SiteProvider = ({ children, isKiosk: initialIsKiosk = false }: SiteProviderProps) => {
    const [searchParams] = useSearchParams();
    const kioskParam = searchParams.get('kiosk') || null;
    const hasKiosk = kioskParam === 'true' || kioskParam === '1';
    const removeKiosk = kioskParam === 'false' || kioskParam === '0';
    const initialKioskValue = JSCookie.get('kiosk') === 'true' || (hasKiosk && !removeKiosk);
    const [isKiosk, setIsKioskState] = useState(initialIsKiosk || initialKioskValue);

    const setKioskCookie = useCallback((val = false) => {
        if (val) {
            JSCookie.set('kiosk', 'true', { expires: 30 });
        } else {
            JSCookie.remove('kiosk');
        }
    }, []);

    useEffect(() => {
        if (hasKiosk) {
            setKioskCookie(true);
        }
        if (removeKiosk) {
            setKioskCookie(false);
        }
    }, [hasKiosk, removeKiosk]);

    const setIsKiosk = useCallback(
        (val = false) => {
            setKioskCookie(val);
            setIsKioskState(val);
        },
        [setKioskCookie, setIsKioskState],
    );

    const value = useMemo(
        () => ({
            isKiosk,
            setIsKiosk,
        }),
        [isKiosk, setIsKiosk],
    );
    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
